import type { SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { Toast } from 'js/src/components/common/Toast';
import { removeLexicalEditor } from 'js/src/data/transcript/transcript-actions';
import { MAX_SCENE_GROUPS_IN_VIDEO } from 'js/src/libs/consts';
import FocusAndScroll from 'js/src/libs/focus-and-scroll';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    deleteSceneGroupOperation,
    duplicateSceneGroupOperation,
    getPrePackagedMorphPosition,
    getPrePackagedSceneSequenceSceneGroupIds,
    getSceneGroupCategory,
} from 'js/src/luminary';
import { CONTENT_SCENE_CATEGORIES } from 'js/src/luminary/consts';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

export const SharedLeftControlHandlers = {
    handleDuplicateSceneGroup: ({
        numSceneGroups,
        duplicateSceneGroup,
        checkpointVideo,
        sceneGroupId,
    }: {
        numSceneGroups: number;
        duplicateSceneGroup: (args: { sceneGroupId: string; parentActionGroup: string }) => void;
        checkpointVideo: (args: { parentActionGroup: string }) => void;
        sceneGroupId: string;
    }) => {
        if (numSceneGroups >= MAX_SCENE_GROUPS_IN_VIDEO) {
            Toast.error(
                `You can have a maximum of ${MAX_SCENE_GROUPS_IN_VIDEO} scenes in your video. To create another scene, you must delete an existing scene.`,
            );
            return;
        }
        const parentActionGroup = uuid();
        duplicateSceneGroup({ sceneGroupId, parentActionGroup });
        checkpointVideo({ parentActionGroup });
    },
    handleMoveUpClicked: ({
        sceneGroupIndex,
        setFocusedSceneInfo,
        moveSceneGroup,
        sceneId,
        sceneGroupId,
        previousSceneId,
    }: {
        sceneGroupIndex: number;
        setFocusedSceneInfo: (sceneInfo: { sceneId: string; sceneGroupId: string }) => void;
        moveSceneGroup: (sceneGroupId: string, targetIndex: number) => void;
        sceneId: string;
        sceneGroupId: string;
        previousSceneId: string | null;
    }) => {
        // if the slide is the top slide, don't move!
        if (sceneGroupIndex <= 0) {
            return;
        }

        const targetIndex = sceneGroupIndex - 1;
        setFocusedSceneInfo({ sceneId, sceneGroupId });
        moveSceneGroup(sceneGroupId, targetIndex);

        // preemptively scroll to the previous slide to make a slick animation
        if (previousSceneId) {
            FocusAndScroll.scrollToSlide(previousSceneId);
        }
    },
    handleMoveDownClicked: ({
        isLastMovableScene,
        sceneGroupIndex,
        setFocusedSceneInfo,
        moveSceneGroup,
        nextSlideId,
        sceneGroupId,
        sceneId,
    }: {
        isLastMovableScene: boolean;
        sceneGroupIndex: number;
        setFocusedSceneInfo: (sceneInfo: { sceneId: string; sceneGroupId: string }) => void;
        moveSceneGroup: (sceneGroupId: string, targetIndex: number) => void;
        nextSlideId: string | null;
        sceneGroupId: string;
        sceneId: string;
    }) => {
        if (isLastMovableScene) {
            return;
        }

        const targetIndex = sceneGroupIndex + 1;
        setFocusedSceneInfo({ sceneId, sceneGroupId });
        moveSceneGroup(sceneGroupId, targetIndex);

        // preemptively scroll to the next slide to make a slick animation
        if (nextSlideId) {
            FocusAndScroll.scrollToSlide(nextSlideId);
        }
    },
    handleDeleteSceneGroup: createLuminaryOperation(
        (
            { call, dispatch, luminary },
            sceneGroupId: string,
            systemTriggered = false,
            parentActionGroup = uuid(),
        ) => {
            // Stop any previews, else any playing preview not related to the current slide will be mis-timed
            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);

            if (!sceneGroup || !SharedLeftControlHandlers.canDelete({ sceneGroup })) {
                Toast.error(
                    'You must have at least one scene in your video! Create a new scene to delete this scene.',
                    'Oops!',
                );
                return;
            }

            const chainIds = getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroupId);
            for (const chainId of chainIds) {
                call(deleteSceneGroupOperation, chainId, systemTriggered, parentActionGroup);
                dispatch(removeLexicalEditor(chainId));
            }
        },
    ),
    duplicatePrePackagedSceneSequence: createLuminaryOperation(
        ({ luminary, call }, sceneGroupId: string, parentActionGroup: string): boolean => {
            const chainIds = getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroupId);
            if (luminary.sceneGroups.length + chainIds.length > MAX_SCENE_GROUPS_IN_VIDEO) {
                Toast.error(
                    `You can have a maximum of ${MAX_SCENE_GROUPS_IN_VIDEO} scenes in your video. To create another scene, you must delete an existing scene.`,
                );
                return false;
            }

            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

            const lastChainGroupId = chainIds.at(-1);
            if (!lastChainGroupId) return false;
            const lastChainGroup = luminary.getSceneGroupById(lastChainGroupId);
            if (!lastChainGroup) return false;
            let insertIndex = lastChainGroup.index + 1;

            for (const sourceId of chainIds) {
                const newId = call(duplicateSceneGroupOperation, {
                    sceneGroupId: sourceId,
                    scrollToNewSceneGroup: false,
                    parentActionGroup,
                });
                if (newId) {
                    luminary.moveSceneGroupToIndex(newId, insertIndex);
                    insertIndex++;
                }
            }
            return true;
        },
    ),
    canMoveUp: ({ sceneGroup }: { sceneGroup: SceneGroup }) => {
        if (sceneGroup.index === 0) return false;
        // Prevent moving if this scene group is part of a pre-packaged scene sequence
        const firstScene = sceneGroup.scenes[0];
        if (firstScene) {
            const position = getPrePackagedMorphPosition(firstScene);
            if (position !== null) return false;
        }
        return true;
    },
    canMoveDown: ({ sceneGroup }: { sceneGroup: SceneGroup }) => {
        const luminary = sceneGroup.root;
        const isLastSceneGroup = sceneGroup.index === luminary.sceneGroups.length - 1;
        if (isLastSceneGroup) {
            return false;
        }
        // Prevent moving if this scene group is part of a pre-packaged scene sequence
        const firstScene = sceneGroup.scenes[0];
        if (firstScene) {
            const position = getPrePackagedMorphPosition(firstScene);
            if (position !== null) return false;
        }
        const nextSceneGroup = luminary.sceneGroups[sceneGroup.index + 1];
        return !nextSceneGroup.scenes.some(
            (scene: { sceneCategory: string | null }) =>
                scene.sceneCategory === VideoData.CATEGORY_BRAND ||
                scene.sceneCategory === VideoData.CATEGORY_OUTRO,
        );
    },
    canDelete: ({ sceneGroup }: { sceneGroup: SceneGroup }) => {
        const luminary = sceneGroup.root;
        const chainIdSet = new Set(
            getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroup.id),
        );
        return luminary.sceneGroups.some(
            (g) =>
                !chainIdSet.has(g.id) &&
                CONTENT_SCENE_CATEGORIES.includes(getSceneGroupCategory(g)!),
        );
    },
};
